// React
import React, { Component } from "react";
import { connect } from "react-redux";
import clsx from "clsx";
// Routing
import { Link, withRouter } from "react-router-dom";
// Material UI
import {
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  Tooltip,
} from "@material-ui/core";
// Vendor
import { getRoleName, isBannerShow, isCampaignLayout } from "@helpers";
// Svg
import { layoutsSvg, sidebarOpenSvg } from "@assets";
// Styles
import { withStyles } from "@material-ui/core/styles";
import "./styles/styles.scss";
import { getSidebarStyles } from "./styles/styles";
// Translations
import { withTranslation } from "react-i18next";
// Chargebee API
import { ChargebeeAPI } from "@api/Chargebee";
// Amplitude API
import { AmplitudeAPI } from "@api/Amplitude";

const useStyles = (theme) => getSidebarStyles(theme, 220);

class MenuSidebar extends Component {
  constructor(props) {
    super(props);
    let cbCustomerId = props.data.user.chargebee_customer_id;
    this.state = {
      cbInstance:
        cbCustomerId != "-"
          ? window.Chargebee.init({
              site: props.data.user.chargebee_customer_site,
            })
          : null,
      platform: process.env.REACT_APP_PLATFORM,
    };
  }

  handlePlatform = (el, index) => {
    const { platform } = this.state;
    switch (platform) {
      case "admin":
        return this.handleAdminItems(el, index);
      case "centribot":
        if (isCampaignLayout(this.props.history)) {
          return this.handleCampaignsItems(el, index);
        }

        return this.handleCentribotItems(el, index);
      case "centridesk":
        return this.handleCentrideskItems(el, index);
    }
  };

  handleCentribotItems = (el, index) => {
    const { actualLayout, limitsIntegrations, actualProject, plan } =
      this.props.data;
    const { classes, open, handleMenuClose, t } = this.props;

    if (
      (el.name === "chatbot" && !actualProject.active) ||
      (plan.plan.name === "consulting_sunco" && el.name === "test_chat") ||
      el.name === "academy" ||
      (el.name === "sandbox" && !actualProject.sandbox_id)
    ) {
      return "";
    }

    if (
      limitsIntegrations &&
      Object.keys(limitsIntegrations).length !== 0 &&
      el.type === "centribot" &&
      (actualProject.active ||
        (!actualProject.active &&
          !["project/create"].includes(this.props.history.location.pathname) &&
          ["stats", "chatbot"].includes(el.name)))
    ) {
      return (
        <Tooltip
          key={index}
          classes={{ tooltip: classes.tooltip }}
          title={t(`commons:sidebars.menu.centribot.${el.name}`)}
          placement="right"
          enterDelay={500}
          leaveDelay={300}
        >
          <Link
            to={this.handleCentribotRoute(el)}
            target={el.new_tab ? "_blank" : ""}
          >
            <ListItem
              onClick={() => handleMenuClose()}
              className={clsx(
                this.handleActiveElement(actualLayout.layout, el.name),
                {
                  [classes.listItemOpen]: open,
                  [classes.listItemClose]: !open,
                }
              )}
            >
              <ListItemIcon
                className={
                  actualLayout.layout === el.name ? el.name : "default"
                }
              >
                {layoutsSvg(el.name)}
              </ListItemIcon>
              {open && (
                <span
                  className={`${classes.text} ${
                    actualLayout.layout === el.name ? el.name : "default"
                  }`}
                >
                  {t(`commons:sidebars.menu.centribot.${el.name}`)}{" "}
                </span>
              )}
            </ListItem>
          </Link>
        </Tooltip>
      );
    }
  };

  handleCampaignsItems = (el, index) => {
    const { actualLayout } = this.props.data;
    const { classes, open, handleMenuClose, t } = this.props;
    return (
      el.type === "campaigns" &&
      this.accessCampaignsModule(el.name) && (
        <Tooltip
          key={index}
          classes={{ tooltip: classes.tooltip }}
          title={t(`commons:sidebars.menu.campaigns.${el.name}`)}
          placement="right"
          enterDelay={500}
          leaveDelay={300}
        >
          <Link
            to={`/campaigns/${el.name}`}
            target={el.new_tab ? "_blank" : ""}
          >
            <ListItem
              onClick={() => handleMenuClose()}
              className={clsx(
                this.handleActiveElement(actualLayout.layout, el.name),
                `${el.disabled ? "disabled_element" : ""}`,
                {
                  [classes.listItemOpen]: open,
                  [classes.listItemClose]: !open,
                }
              )}
            >
              <ListItemIcon
                className={
                  actualLayout.layout === el.name ? el.name : "default"
                }
              >
                {layoutsSvg(el.name)}
              </ListItemIcon>
              {open && (
                <span
                  className={`${classes.text} ${
                    actualLayout.layout === el.name ? el.name : "default"
                  }`}
                >
                  {t(`commons:sidebars.menu.campaigns.${el.name}`)}{" "}
                </span>
              )}
            </ListItem>
          </Link>
        </Tooltip>
      )
    );
  };

  accessCampaignsModule = (module) => {
    const { user } = this.props.data;

    switch (user.role_name || user.role.name) {
      case "SuperAdmin":
      case "Administrator":
        return true;
      case "Agent":
      case "Supervisor":
        const access = user?.campaigns?.access;
        return access[module] || (module === "webhook" && access.individual);
      default:
        return false;
    }
  };

  handleCentrideskItems = (el, index) => {
    const { actualLayout } = this.props.data;
    const { classes, open, t } = this.props;
    return (
      <Tooltip
        key={index}
        classes={{ tooltip: classes.tooltip }}
        title={t(`commons:sidebars.menu.centridesk.${el.name}`)}
        placement="right"
        enterDelay={500}
        leaveDelay={300}
      >
        <Link to={`/${el.name}`}>
          <ListItem
            onClick={() => {
              this.handleCentrideskRoute(el);
            }}
            className={clsx(this.handleCentrideskActiveElement(el.name), {
              [classes.listItemOpen]: open,
              [classes.listItemClose]: !open,
            })}
          >
            <ListItemIcon
              className={
                this.handleCentrideskActiveElement(el.name)
                  ? el.name
                  : "default"
              }
            >
              {layoutsSvg(el.name)}
            </ListItemIcon>
            {open && (
              <span className={`${classes.text}`}>
                {t(`commons:sidebars.menu.centridesk.${el.name}`)}
              </span>
            )}
          </ListItem>
        </Link>
      </Tooltip>
    );
  };

  handleAdminItems = (el, index) => {
    const { classes, open, t } = this.props;
    const { user, plan } = this.props.data,
      accountsList = [
        "4a8b56cb941248eebc3b19d2b71c70ab",
        "1f842ec122f94055afef189d8699be95",
        "eda8c8a6ea1342e3a03539bc3131b861",
        "d1a7a881424140908a8ce748a899af50",
        "77dc0ace8a38453ca477eb1efe1a246a",
        "d7ad104be2ad4361987a47fc521a1268",
        "9201694efd4c4abfa2dcfb8e2279be5f",
        "50c310d0f0544acaaa99a84c7365ddfb",
      ];

    const isUserSuperAdmin = user.role_name === "SuperAdministrator";
    const plansHidden =
      plan?.plan?.name === "consulting_sunco" ||
      plan?.plan?.name === "consulting_enterprise" ||
      plan?.plan?.name === "consulting_professional";

    if (
      plansHidden &&
      isUserSuperAdmin &&
      (el.name === "plans" || el.name === "subscription")
    ) {
      return "";
    }

    if (
      user.username === "gonzalo.berrmejo@makro.es" &&
      (el.name === "plans" || el.name === "subscription")
    ) {
      return "";
    }

    var is_accesible_to_integrations = false;

    if (
      el.name === "integrations" &&
      (plan?.plan?.name === "consulting_sunco" ||
        accountsList.includes(user.account_id))
    ) {
      is_accesible_to_integrations = true;
    }

    if (!is_accesible_to_integrations && el.name === "integrations") {
      return "";
    }

    if (
      (["Supervisor", "Administrator"].includes(user.role_name) &&
        (el.name === "plans" || el.name === "subscription")) ||
      (user.role_name === "Supervisor" && el.name === "integrations")
    ) {
      return "";
    }

    return (
      <Tooltip
        key={index}
        classes={{ tooltip: classes.tooltip }}
        title={t(`commons:sidebars.menu.admin.${el.name}`)}
        placement="right"
        enterDelay={500}
        leaveDelay={300}
      >
        <ListItem
          onClick={() => {
            this.handleAdminRoute(el);
          }}
          className={clsx(this.handleAdminActiveElement(el.name), {
            [classes.listItemOpen]: open,
            [classes.listItemClose]: !open,
          })}
        >
          <ListItemIcon className={el.name}>{layoutsSvg(el.name)}</ListItemIcon>
          {open && (
            <span className={`${classes.text}`}>
              {t(`commons:sidebars.menu.admin.${el.name}`)}
            </span>
          )}
        </ListItem>
      </Tooltip>
    );
  };

  handleActiveElement = (layout, name) => {
    if (layout === name) {
      return "active_element";
    }
  };

  handleAdminActiveElement = (layout) => {
    const { history } = this.props;
    const { plan } = this.props.data;

    const plansHidden =
      plan?.plan?.name === "consulting_sunco" ||
      plan?.plan?.name === "consulting_enterprise" ||
      plan?.plan?.name === "consulting_professional";
    if (
      (plansHidden
        ? history?.location?.pathname === "/" && layout === "users"
        : history?.location?.pathname === "/" && layout === "plans") ||
      (history?.location?.pathname === "/" && layout === "plans") ||
      (history?.location?.pathname.includes("users") && layout === "users") ||
      (history?.location?.pathname.includes("groups") && layout === "groups") ||
      (history?.location?.pathname === "/billing" &&
        layout === "subscription") ||
      (history?.location?.pathname.includes("integrations") &&
        layout === "integrations")
    ) {
      return "active_element";
    }
  };

  handleCentrideskActiveElement = (layout) => {
    const { history } = this.props;
    if (
      (history?.location?.pathname === "/tickets" && layout === "tickets") ||
      (history?.location?.pathname === "/my-tickets" &&
        layout === "my-tickets") ||
      (history?.location?.pathname.includes("conversations") &&
        layout === "conversations") ||
      (history?.location?.pathname.includes("admin") && layout === "admin")
    ) {
      return "active_element";
    }
  };

  handleCentribotRoute = (layout) => {
    const { actualProject } = this.props.data;
    var url;
    if (actualProject.id) {
      switch (layout?.name) {
        case "chatbot":
          url = `/${actualProject.id}/chatbot`;
          break;
        case "test_chat":
          url = `/test_chat?project_id=${actualProject.id}&chat_id=${actualProject.chat_id}`;
          break;
        case "sandbox":
          url = `/${actualProject.sandbox_id}/chatbot`;
          break;
        case "help_chat":
          url = `/help_chat`;
          break;
        default:
          url = `/${actualProject.id}/${layout.name}`;
          break;
      }
    } else {
      url = "/project";
    }

    return url;
  };

  handleCentrideskRoute = (layout) => {
    const { handleMenuClose } = this.props;
    this.handleAmplitude(`Click on Desk ${layout.name}`);
    handleMenuClose();
  };

  handleAdminRoute = (layout) => {
    const { history, handleMenuClose, user } = this.props;
    var event_type = layout?.name;
    switch (layout?.name) {
      case "plans":
        history.push("/");
        break;
      case "users":
        history.push(`/${layout.name}`);
        break;
      case "groups":
        history.push("/groups");
        break;
      case "subscription":
        if (user.chargebee_customer_id !== "-") {
          this.handleShowPortal();
        } else {
          event_type = "billing";
          history.push("/billing");
        }
        break;
      case "integrations":
        history.push("/integrations");
        break;
    }

    this.handleAmplitude(`Click on ${event_type}`);

    handleMenuClose();
  };

  handleAmplitude = (event_type) => {
    const { user, device_id } = this.props.data;

    new AmplitudeAPI({
      event_type: event_type,
      device_id: device_id,
      user_id: user.email,
    }).eventLog();
  };

  handleVisibility = () => {
    const { history } = this.props;
    const { platform } = this.state;
    const { plan, user, actualProject } = this.props.data;
    var res = true;
    switch (true) {
      case history.location.pathname.includes("/academy"):
        res = false;
        break;
      case history.location.pathname.includes("campaigns") &&
        platform !== "admin":
        res = plan?.whatsapp_phones?.length > 0 && plan?.platform?.centripush;
        break;
    }

    if (
      platform === "centribot" &&
      getRoleName(user.roles[0]) === "Trainer" &&
      !actualProject.active
    ) {
      res = false;
    }
    return res;
  };

  handleShowPortal = async () => {
    const { user } = this.props;
    const { access, auth } = this.props.data;
    await this.state.cbInstance.setPortalSession(() => {
      let data = JSON.stringify({
        chargebee_customer_id: user.chargebee_customer_id,
      });
      let chargebee_api = new ChargebeeAPI(
        user.id,
        access ? access : auth.access,
        this.props,
        data
      );
      return chargebee_api.get_chargebee_user_portal();
    });

    let chargebee_logout = () => this.state.cbInstance.logout();

    this.state.cbInstance.createChargebeePortal().open({
      close: function () {
        chargebee_logout();
      },
    });
  };

  render() {
    const { layouts, plan, account, user } = this.props.data;
    const { classes, open, handleMenuToggle } = this.props;
    return (
      this.handleVisibility() && (
        <Drawer
          variant="permanent"
          className={`${clsx(classes.drawer, {
            [classes.drawerOpen]: open,
            [classes.drawerClose]: !open,
          })} ${
            isBannerShow(plan ?? account, user) ? "expiration" : ""
          } platform_menu_sidebar`}
          classes={{
            paper: clsx(classes.paper, {
              [classes.drawerOpen]: open,
              [classes.drawerClose]: !open,
            }),
          }}
        >
          {
            <React.Fragment>
              <List className={classes.list}>
                {layouts.map((el, index) => this.handlePlatform(el, index))}
              </List>
              <ListItem
                onClick={handleMenuToggle}
                className={clsx({
                  [classes.listItemOpen]: open,
                  [classes.listItemClose]: !open,
                })}
              >
                <ListItemIcon>{sidebarOpenSvg(open)}</ListItemIcon>
              </ListItem>
            </React.Fragment>
          }
        </Drawer>
      )
    );
  }
}

const mapStateToProps = (state) => {
  return {
    data: state,
  };
};

const connect_sidebar = connect(mapStateToProps)(MenuSidebar);

export default withRouter(
  withTranslation()(withStyles(useStyles)(connect_sidebar))
);
